import { BrowserSupport } from '@tableau/browser-support';
import _ from 'underscore';

import { setVizPath } from '~src/app/actions/spaces/SetVizPathAction';
import { getAppStore } from '~src/app/AppStore';
import { UrlParamKeys } from '~src/app/UrlParamKeys';
import { IDataAlertInfo } from '~src/dda/models/IDataAlertInfo';
import { DevicePreviewService } from '~src/devicePreview/services/DevicePreviewService';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { decodeSpecialSlash, encodeSpecialSlash, getReplacerForUserSlashKeep } from '~src/ts/helpers/QueryParameterHelper';
import { getVizPath } from '~src/ts/helpers/UrlHelpers';
import { DeviceType } from '~src/ts/models/DeviceType';
import { IPreviewDeviceSize } from '~src/ts/models/IPreviewDeviceSize';
import { ISize } from '~src/ts/models/ISize';
import { IViz as VizModel, VizType } from '~src/ts/models/Viz';

import { VizService } from './VizService';

/**
 * This class was ported over from tbViz.ts
 */

export type VizProps = {
  viz: VizModel;
  dalert: IDataAlertInfo;  // this has to be lowercased, for some reason uppercased (dataAlert) does not work
  onVizPathChange: (newPath: string) => void;
  onVizInstanceIdChange: (newInstanceId: string) => void;
  onVizIsExplicitOriginalVizChange: (isExplicitOriginalViz: boolean) => void;
  clearDataAlertRoute: () => void;
};

export namespace VizView {
  export const loadViz = (props: VizProps, container: HTMLElement) => {
    const deviceTypeOfDataAlert: DeviceType = props.dalert?.deviceType ?? 'default';
    const vizService = new VizService();

    let viz: tb.IViz;

    // Toolbar should always be on top in VizPortal
    const vizCreateOptions: tb.IVizCreateOptions = {
      toolbarPosition: tableau.ToolbarPosition.TOP,
      instanceIdToClone: props.viz.instanceId,
      // Note!!! When user reloads the webpage the navigation type is 'reload', but changes to 'navigate' by the time vizportal page is done displaying.
      // So need to capture the state before any navigations by script or framework.
      // See also comments in VizParameters.cs that sets navSrc.
      navType: window.performance.navigation.type.toString(),
      onFirstInteractive: () => {
        props.onVizInstanceIdChange(viz.getInstanceId());

        // TODO: in tbViz, it emits VizLoadComplete event, looks like it is not needed anymore 1238128
      }
    };

    // Include any custom viz parameters in the viz create options
    const params = _.extend(vizCreateOptions, props.viz.params);

    //If in edit data alert mode with nondefault device layout, ignore value of :device URL param
    //default isn't supported so we leave it blank if default
    if (deviceTypeOfDataAlert !== 'default') {
      //getDataAlerts returns the device type in Upper case, so we need to convert it to lowercase before
      //using it in the viz URL
      params[UrlParamKeys.DeviceType] = deviceTypeOfDataAlert.toString().toLowerCase();
    }

    params[':openAuthoringInTopWindow'] = true;
    params[':browserBackButtonUndo'] = true;
    params[':commentingEnabled'] = true;
    params[':reloadOnCustomViewSave'] = true;
    params[':showAppBanner'] = false;
    params[':isVizPortal'] = true;
    // TFSID: 610157
    const shareFlag = vizService.isShowShareParamOn();
    // Display the share options if the viz is a View type
    if (props.viz.vizType === VizType.View && shareFlag !== null) {
      params[':showShareOptions'] = shareFlag;
    }

    vizCreateOptions.width = '100%';
    vizCreateOptions.height = '100%';

    if (props.viz.vizType === VizType.DeviceVizPreview) {
      const size: IPreviewDeviceSize = DevicePreviewService.getSize();
      const device: DeviceType = DevicePreviewService.getPreviewDevice();

      const isDesktopSize: boolean = device === 'desktop';
      params[':commentingEnabled'] = false;
      params[':devicepreviewmode'] = true;
      params[':mobile'] = !isDesktopSize;
      params[UrlParamKeys.DeviceType] = device;
      if (FeatureFlags.SheetsAsTabsDashboardsPreviewLikePhoenix) {
        // TFS 852547 - Sheets as Tabs Dashboard should preview like Phoenix
        // Currently, there is a discrepancy between Mobile Web/Ackbar and Phoenix
        // When dashboards have sheets as tabs - Phoenix does not show the tabs.  Mobile Web/Ackbar does.
        // We are aligning with Phoenix on phone and tablet by removing the tabs.
        if (device === 'phone' || device === 'tablet') {
          params[':tabs'] = '0';
        }
      }

      vizCreateOptions.hideToolbar = true;
      vizCreateOptions.width = size.width;
      vizCreateOptions.height = size.height;

      // Hiding the scrollbars at first to get the real available size.  Eventually, overflow-x -y are manually set
      // to scroll.
      // This line is here because if the size is set first, streaks may appear in Safari when the
      // browser is zoomed out. TFS840813
      // overflow-x and -y are set separately because IE and Edge require separate declarations to reset
      // each.``

      container.style.overflowX = 'hidden';
      container.style.overflowY = 'hidden';

      container.style.height = size.height;
      container.style.width = size.width;
      container.style.padding = '0';

      vizCreateOptions.onFirstVizSizeKnown = (evt: tb.IFirstVizSizeKnownEvent) => {
        const vizSize = evt.getVizSize();
        const sheetSize = vizSize.sheetSize;
        const scrollbarSize = BrowserSupport.ScrollbarWidth();

        const actualSizeAfterShrink: ISize = DevicePreviewService.getShrunkenSizeForPhone(sheetSize);
        if (actualSizeAfterShrink !== null) {
          viz.setFrameSize(actualSizeAfterShrink.width, actualSizeAfterShrink.height);
        } else {
          viz.refreshSize();
        }

        // Pushing scrollbars outside the preview box to increase viewable space - TFS837062
        // Also fixes the problem IE has in using the clientWidth for rendering the horizontal scrollbar - TFS846457
        // overflow is set manually on both axes to control the incidental y-scroll that appears when padding is added as a
        // result of the x-scroll and vice versa
        const iFrameElement = container.getElementsByTagName('iframe')[0];

        const shouldHaveVerticalScrollbar: boolean = container.clientHeight < iFrameElement.offsetHeight;
        const shouldHaveHorizontalScrollbar: boolean = container.clientWidth < iFrameElement.offsetWidth;

        if (shouldHaveVerticalScrollbar) {
          container.style.paddingRight = '' + scrollbarSize;
          container.style.overflowY = 'scroll';
        }
        if (shouldHaveHorizontalScrollbar) {
          container.style.paddingBottom = '' + scrollbarSize;
          container.style.overflowX = 'scroll';
        }
      };
    }

    const uri = vizService.getUri(props.viz);
    viz = new tableau.Viz(container, uri, params);

    // note: The event handlers below are to update the url from changes in the viz, such as tab
    // switches or custom view loads. Ideally the JS API would tell us when the url changes in a
    // URL_CHANGED event, but we didn't have enough time to do that for 9.0.
    // warning: the custom view events fire at very strange times, for example see BUGZID: 132554
    const urlChangeEvents = [
      tableau.TableauEventName.CUSTOM_VIEW_LOAD,
      tableau.TableauEventName.CUSTOM_VIEW_REMOVE,
      tableau.TableauEventName.CUSTOM_VIEW_SAVE,
      tableau.TableauEventName.TAB_SWITCH
    ];

    _.each(urlChangeEvents, (eventName: tb.TableauEventNameEnum) => {
      viz.addEventListener(eventName, (e: tb.ICustomViewEvent) => {
        viz.getCurrentUrlAsync().then((url: string) => {
          // when changing path via tabs, update the app state which is used for the Provider
          // to update the url (see AdminAnalysisVizSpaceProvider for an example)
          // Do NOT dispatch this for device preview vizzes, because those are not controlled
          // by React Router and we do not want those to update the url

          var newPath = getVizPath(url);
          // TFSID 1240198: If the URL has a user name with a slash, the slash is encoded at this point. But we have
          // to decode the URL, which means the we won't be able to differentiate the user name's slashes with the
          // slashes used to separate the path in the URL. To avoid this, if we have an encoded slash in the URL,
          // use our own temporary special encoding for the encoded slash, and then restore after everything else
          // is decoded.
          var tempSlashReplacer = getReplacerForUserSlashKeep(newPath);
          if (tempSlashReplacer != null) {
            newPath = encodeSpecialSlash(newPath, tempSlashReplacer);
          }
          newPath = decodeURIComponent(newPath);
          if (tempSlashReplacer != null) {
            newPath = decodeSpecialSlash(newPath, tempSlashReplacer);
          }

          if (props.viz.vizType !== VizType.DeviceVizPreview) {
            getAppStore().dispatch(setVizPath(newPath));
          }

          props.onVizInstanceIdChange(viz.getInstanceId());

          if (props.viz.path !== newPath) {
            props.onVizPathChange(newPath);
          }

          const isExplicitOriginalViz = vizService.getIsExplicitOriginalVizFromUrl(url);
          props.onVizIsExplicitOriginalVizChange(isExplicitOriginalViz);
        });
      });
    });

    /// TFSID: 629994 Receive a postMessage event from vizclient iframe to remove :editDataAlert URL parameter
    /// This is a undocumented, private event that we're not exposing publicly via the JS API client library.
    /// Thus, we need to use our own postMessage event handler instead of using viz.addEventListener
    /// See https://mytableau.tableaucorp.com/x/4PijBg for details
    window.addEventListener('message', (event: any) => {
      if (event.data != null && typeof (event.data) === 'string'
        && (event.data.indexOf('RemoveEditDataAlertUrlParamEvent') > 0)) {
        delete props.viz.params[UrlParamKeys.EditDataAlert];
        delete props.viz.params[UrlParamKeys.ResumeDataAlert];

        props.clearDataAlertRoute();
      }

    });

    return viz;
  };
}

export type VizView = typeof VizView;
